import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchEmissions } from "@/api/emissionsService";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, isWithinInterval, subDays } from "date-fns";
import { Leaf, ChevronLeft, ChevronRight, Download, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BookingDetailsDialog } from "@/components/BookingDetailsDialog";
import DashboardFiltersComponent, { DashboardFilters } from "@/pages/dashboard/components/DashboardFilters";

const PAGE_SIZE = 25;

const columns = [
  { id: "user", label: "User" },
  { id: "kg_co2_saved", label: "CO2 Saved (kg)" },
  { id: "kg_ch4_saved", label: "CH4 Saved (kg)" },
  { id: "kg_n2o_saved", label: "N2O Saved (kg)" },
  { id: "kg_co2_emitted", label: "CO2 Emitted (kg)" },
  { id: "kg_ch4_emitted", label: "CH4 Emitted (kg)" },
  { id: "kg_n2o_emitted", label: "N2O Emitted (kg)" },
  { id: "co2e_co2", label: "CO2e CO2 (kg)" },
  { id: "co2e_ch4", label: "CO2e CH4 (kg)" },
  { id: "co2e_n2o", label: "CO2e N2O (kg)" },
  { id: "total_co2e", label: "Total CO2e (kg)" },
  { id: "created_at", label: "Ride Date" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "unconfirmed", label: "Unconfirmed" },
  { value: "confirmed", label: "Confirmed" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "unavailable", label: "Unavailable" },
];

const TIME_PERIOD_OPTIONS = [
  { value: "all", label: "All Time" },
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "year", label: "Year" },
];

const EmissionsPage = () => {
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [timePeriod, setTimePeriod] = useState("all");
  const [locationFilters, setLocationFilters] = useState<DashboardFilters>({
    regionId: "all",
    organizationId: "all",
    siteId: "all"
  });
  const [now] = useState(() => new Date());
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);

  // Helper function to determine site IDs for filtering
  const getSiteIdsForFilters = async (filters: DashboardFilters): Promise<string[] | null> => {
    if (filters.siteId && filters.siteId !== 'all') {
      return [filters.siteId];
    } else if (filters.organizationId && filters.organizationId !== 'all') {
      const { data: orgSites } = await supabase
        .from('sites')
        .select('id')
        .eq('organization_id', filters.organizationId);
      
      return orgSites && orgSites.length > 0 ? orgSites.map(site => site.id) : null;
    } else if (filters.regionId && filters.regionId !== 'all') {
      const { data: regionOrgs } = await supabase
        .from('organizations')
        .select('id')
        .eq('region_id', filters.regionId);
      
      if (regionOrgs && regionOrgs.length > 0) {
        const orgIds = regionOrgs.map(org => org.id);
        const { data: regionSites } = await supabase
          .from('sites')
          .select('id')
          .in('organization_id', orgIds);
        
        return regionSites && regionSites.length > 0 ? regionSites.map(site => site.id) : null;
      }
    }
    return null;
  };

  const { data, isLoading } = useQuery({
    queryKey: ["emissions", page, statusFilter, timePeriod, locationFilters],
    queryFn: async () => {
      console.log("Fetching emissions with filters", { statusFilter, timePeriod, locationFilters });
      
      const emissions = await fetchEmissions();
      
      return {
        emissions: emissions || [],
        total: emissions?.length || 0,
      };
    },
    refetchOnWindowFocus: false,
  });

  const [siteIdsForFiltering, setSiteIdsForFiltering] = useState<string[] | null>(null);

  // Update site IDs when location filters change
  useEffect(() => {
    const updateSiteIds = async () => {
      const siteIds = await getSiteIdsForFilters(locationFilters);
      setSiteIdsForFiltering(siteIds);
    };
    updateSiteIds();
  }, [locationFilters]);

  const filteredEmissions = (data?.emissions || []).filter((emission) => {
    // Status filter
    const statusMatch = statusFilter === "all" || emission.ride?.booking?.status === statusFilter;
    
    // Location filter
    let locationMatch = true;
    if (siteIdsForFiltering && siteIdsForFiltering.length > 0) {
      // Filter by the calculated site IDs
      locationMatch = siteIdsForFiltering.includes(emission.ride?.booking?.start_location_id || '') ||
                     siteIdsForFiltering.includes(emission.ride?.booking?.end_location_id || '');
    }
    
    // Time filter
    let timeMatch = true;
    if (timePeriod !== "all") {
      const created = new Date(emission.created_at);
      let range = null;
      switch (timePeriod) {
        case "day":
          range = { start: startOfDay(now), end: endOfDay(now) };
          break;
        case "week":
          range = { start: startOfDay(subDays(now, 6)), end: endOfDay(now) };
          break;
        case "month":
          range = { start: startOfMonth(now), end: endOfMonth(now) };
          break;
        case "year":
          range = { start: startOfYear(now), end: endOfYear(now) };
          break;
        default:
          break;
      }
      if (range) {
        timeMatch = isWithinInterval(created, range);
      }
    }
    
    return statusMatch && locationMatch && timeMatch;
  });

  const total = filteredEmissions.length;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const emissions = filteredEmissions.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleUserClick = (booking) => {
    setSelectedBooking(booking);
    setBookingDialogOpen(true);
  };

  const renderCell = (emission, attribute) => {
    switch (attribute) {
      case "user": {
        const username = emission.ride?.booking?.user?.username;
        const booking = emission.ride?.booking;
        return (
          <div className="font-medium">
            {username && booking ? (
              <button
                onClick={() => handleUserClick(booking)}
                className="text-primary hover:underline cursor-pointer"
              >
                {username}
              </button>
            ) : (
              <span className="text-muted-foreground">-</span>
            )}
          </div>
        );
      }
      case "kg_co2_saved":
        return (
          <span className="font-medium text-green-600">
            {emission.kg_co2_saved ? emission.kg_co2_saved.toFixed(3) : "0.000"}
          </span>
        );
      case "kg_ch4_saved":
        return (
          <span className="font-medium text-green-600">
            {emission.kg_ch4_saved ? emission.kg_ch4_saved.toFixed(3) : "0.000"}
          </span>
        );
      case "kg_n2o_saved":
        return (
          <span className="font-medium text-green-600">
            {emission.kg_n2o_saved ? emission.kg_n2o_saved.toFixed(3) : "0.000"}
          </span>
        );
      case "kg_co2_emitted":
        return (
          <span className="font-medium text-red-600">
            {emission.kg_co2_emitted ? emission.kg_co2_emitted.toFixed(3) : "0.000"}
          </span>
        );
      case "kg_ch4_emitted":
        return (
          <span className="font-medium text-red-600">
            {emission.kg_ch4_emitted ? emission.kg_ch4_emitted.toFixed(3) : "0.000"}
          </span>
        );
      case "kg_n2o_emitted":
        return (
          <span className="font-medium text-red-600">
            {emission.kg_n2o_emitted ? emission.kg_n2o_emitted.toFixed(3) : "0.000"}
          </span>
        );
      case "co2e_co2":
        return (
          <span className="font-medium text-blue-600">
            {emission.co2e_co2 ? emission.co2e_co2.toFixed(3) : "0.000"}
          </span>
        );
      case "co2e_ch4":
        return (
          <span className="font-medium text-blue-600">
            {emission.co2e_ch4 ? emission.co2e_ch4.toFixed(3) : "0.000"}
          </span>
        );
      case "co2e_n2o":
        return (
          <span className="font-medium text-blue-600">
            {emission.co2e_n2o ? emission.co2e_n2o.toFixed(3) : "0.000"}
          </span>
        );
      case "total_co2e": {
        const totalCO2e = (emission.co2e_co2 || 0) + (emission.co2e_ch4 || 0) + (emission.co2e_n2o || 0);
        return (
          <span className="font-medium text-purple-600">
            {totalCO2e.toFixed(3)}
          </span>
        );
      }
      case "created_at":
        return (
          <div>
            <div className="font-medium">{format(new Date(emission.created_at), "dd MMM yyyy")}</div>
            <div className="text-xs text-muted-foreground">
              {format(new Date(emission.created_at), "HH:mm")}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const csvContent = [
        columns.map((col) => col.label).join(","),
        ...filteredEmissions.map((emission) => [
          emission.ride?.booking?.user?.username || "",
          emission.kg_co2_saved || 0,
          emission.kg_ch4_saved || 0,
          emission.kg_n2o_saved || 0,
          emission.kg_co2_emitted || 0,
          emission.kg_ch4_emitted || 0,
          emission.kg_n2o_emitted || 0,
          emission.co2e_co2 || 0,
          emission.co2e_ch4 || 0,
          emission.co2e_n2o || 0,
          (emission.co2e_co2 || 0) + (emission.co2e_ch4 || 0) + (emission.co2e_n2o || 0),
          format(new Date(emission.created_at), "yyyy-MM-dd HH:mm"),
        ].join(","))
      ].join("\n");

      const csvBlob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const csvUrl = URL.createObjectURL(csvBlob);
      const csvLink = document.createElement("a");
      csvLink.href = csvUrl;
      csvLink.setAttribute("download", `emissions_export_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(csvLink);
      csvLink.click();
      document.body.removeChild(csvLink);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Leaf className="h-6 w-6 text-green-600" />
          <h1 className="text-3xl font-bold tracking-tight">Emissions</h1>
        </div>
        <Button onClick={handleExportCSV} disabled={exporting || isLoading} variant="outline" size="sm">
          <Download className="mr-2 h-4 w-4" />
          {exporting ? "Exporting..." : "Download CSV"}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Emissions Data</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Location Filters */}
          <div className="mb-4">
            <DashboardFiltersComponent 
              filters={locationFilters}
              onFiltersChange={(filters) => {
                setLocationFilters(filters);
                setPage(1); // Reset to first page when filters change
              }}
            />
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-end mb-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <label htmlFor="status" className="font-medium min-w-max text-muted-foreground text-sm">Status:</label>
              <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="period" className="font-medium min-w-max text-muted-foreground text-sm">Period:</label>
              <Select value={timePeriod} onValueChange={v => { setTimePeriod(v); setPage(1); }}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="All Time" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_PERIOD_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  {columns.map((column) => (
                    <TableHead key={column.id}>{column.label}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="text-center">
                      <div className="flex items-center justify-center py-4">
                        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                        <span className="ml-2">Loading emissions...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : !emissions || emissions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="text-center py-8">
                      <div className="flex flex-col items-center justify-center">
                        <Leaf className="h-12 w-12 text-muted-foreground mb-2" />
                        <p className="text-muted-foreground font-medium">No emissions data found</p>
                        <p className="text-xs text-muted-foreground">There are no emissions records to display at this time.</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  emissions.map((emission) => (
                    <TableRow key={emission.id} className="hover:bg-muted/50">
                      {columns.map((column) => (
                        <TableCell key={`${emission.id}-${column.id}`}>{renderCell(emission, column.id)}</TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center mt-4 gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                aria-label="Previous"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              {(() => {
                const pages = [];
                const maxPagesToShow = 5;
                let start = Math.max(1, page - 2);
                let end = Math.min(totalPages, page + 2);
                if (page <= 3) {
                  end = Math.min(totalPages, maxPagesToShow);
                } else if (page >= totalPages - 2) {
                  start = Math.max(1, totalPages - maxPagesToShow + 1);
                }
                if (start > 1) {
                  pages.push(
                    <button
                      key={1}
                      className={`px-2 py-1 rounded ${page === 1 ? 'bg-muted font-bold' : 'hover:bg-muted'}`}
                      onClick={() => setPage(1)}
                    >1</button>
                  );
                  if (start > 2) pages.push(<span key="start-ellipsis" className="px-1">...</span>);
                }
                for (let i = start; i <= end; i++) {
                  pages.push(
                    <button
                      key={i}
                      className={`px-2 py-1 rounded ${page === i ? 'bg-muted font-bold' : 'hover:bg-muted'}`}
                      onClick={() => setPage(i)}
                      disabled={page === i}
                    >{i}</button>
                  );
                }
                if (end < totalPages) {
                  if (end < totalPages - 1) pages.push(<span key="end-ellipsis" className="px-1">...</span>);
                  pages.push(
                    <button
                      key={totalPages}
                      className={`px-2 py-1 rounded ${page === totalPages ? 'bg-muted font-bold' : 'hover:bg-muted'}`}
                      onClick={() => setPage(totalPages)}
                    >{totalPages}</button>
                  );
                }
                return pages;
              })()}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages || totalPages === 0}
                aria-label="Next"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <BookingDetailsDialog
        isOpen={bookingDialogOpen}
        onOpenChange={setBookingDialogOpen}
        booking={selectedBooking}
      />
    </div>
  );
};

export default EmissionsPage; 